import { Button } from '@components/primitives/Button';
import { useOrganizationId } from '@features/user';
import { useCopyToClipboard } from '@hooks/useCopyToClipboard';
import LinkOutlinedIcon from '@mui/icons-material/LinkOutlined';
import { Tooltip } from '@mui/material';
import { FeatureGates, useEventTracking, useFeatureGate } from '@services/statsig';

/** Calendar-header shortcut that copies the practice's public self-serve
 * booking URL ({forms domain}/book/{organization_id}) to the clipboard.
 *
 * Gated on the self-scheduling feature only — no calendar-manage permission,
 * since the URL is public by design and front-desk staff sharing it with
 * patients is the primary use. (GDN-2414)
 */
export function BookingPageLinkButton() {
  const schedulingFeatureEnabled = useFeatureGate(
    FeatureGates.PATIENT_SELF_SCHEDULING,
  );
  const organizationId = useOrganizationId();
  const copy = useCopyToClipboard();
  const { schedulingSettings } = useEventTracking();

  if (!schedulingFeatureEnabled || !organizationId) {
    return null;
  }

  const formsBaseUrl =
    (import.meta.env.VITE_FORMS_URL as string | undefined) ||
    'https://forms.jotpsych.com';
  const bookingUrl = `${formsBaseUrl}/book/${organizationId}`;

  const handleCopy = () => {
    copy(bookingUrl, 'Booking page link');
    schedulingSettings.bookingPageLinkCopied();
  };

  return (
    <Tooltip title={`Copy your practice's public booking link: ${bookingUrl}`}>
      <Button
        variant="outlined"
        color="inherit"
        size="small"
        startIcon={<LinkOutlinedIcon />}
        onClick={handleCopy}
        sx={{ whiteSpace: 'nowrap' }}
      >
        Booking page
      </Button>
    </Tooltip>
  );
}
